import {
  ArrowLeft,
  Check,
  LoaderCircle,
  LogOut,
  QrCode,
  UserRound,
  X,
} from 'lucide-react';
import type {
  AuthFormMode,
  ScanChannel,
  ScanTicketStatus,
} from '../../shared/types';
import type { UserCenterAuthForm } from './UserCenterDrawer';
import { PasswordField } from './PasswordField';

export type AuthGateEntryMode = 'manual' | 'scan';

interface AuthGateProps {
  open: boolean;
  previewMode: boolean;
  interactiveMode: boolean;
  authenticated: boolean;
  authenticatedDisplayName: string;
  authenticatedEmail: string | null;
  authenticatedDisplayId: string | null;
  needsEmailBinding: boolean;
  showAuthenticatedSummary: boolean;
  manualNotice?: string | null;
  authMode: AuthFormMode;
  entryMode: AuthGateEntryMode;
  authForm: UserCenterAuthForm;
  authBusy: boolean;
  otpBusy: boolean;
  otpCooldownSeconds: number;
  logoutBusy: boolean;
  scanBusy: boolean;
  scanCodeUrl: string | null;
  scanCodeChannel: ScanChannel | null;
  scanChannel: ScanChannel;
  scanStatus: ScanTicketStatus | null;
  scanStatusTitle: string;
  scanStatusHint: string;
  scanError: string | null;
  deviceId: string;
  licenseLabel: string;
  remainingLabel: string;
  cardLimitLabel: string;
  overlayNotice: string | null;
  onClose: () => void;
  onEntryModeChange: (mode: AuthGateEntryMode) => void;
  onAuthModeChange: (mode: AuthFormMode) => void;
  onAuthFormChange: (patch: Partial<UserCenterAuthForm>) => void;
  onSubmitAuth: () => void;
  onSendOtp: () => void;
  onLogout: () => void;
  onOpenLicenseCenter: () => void;
  onOpenWebsite: () => void;
  onOpenNiuq: () => void;
  onOpenDoubao: () => void;
  onOpenMiniProgramPreview: () => void;
  onRetryScan: () => void;
  onScanChannelChange: (channel: ScanChannel) => void;
}

const getPrimaryLabel = (mode: AuthFormMode): string => {
  switch (mode) {
    case 'register':
      return '注册';
    case 'forgot':
      return '重置密码';
    default:
      return '登录';
  }
};

export function AuthGate({
  open,
  previewMode,
  interactiveMode,
  authenticated,
  authenticatedDisplayName,
  authenticatedEmail,
  authenticatedDisplayId,
  showAuthenticatedSummary,
  manualNotice,
  authMode,
  entryMode,
  authForm,
  authBusy,
  otpBusy,
  otpCooldownSeconds,
  logoutBusy,
  scanBusy,
  scanCodeUrl,
  scanCodeChannel,
  scanChannel,
  scanStatus,
  scanStatusTitle,
  scanStatusHint,
  scanError,
  onClose,
  onEntryModeChange,
  onAuthModeChange,
  onAuthFormChange,
  onSubmitAuth,
  onSendOtp,
  onLogout,
  onOpenLicenseCenter,
  onOpenWebsite,
  onOpenNiuq,
  onOpenDoubao,
  onOpenMiniProgramPreview,
  onRetryScan,
  onScanChannelChange,
}: AuthGateProps) {
  if (!open) {
    return null;
  }

  const closeable = previewMode || interactiveMode;
  const subtitleParts = [
    authenticatedDisplayId ? `ID ${authenticatedDisplayId}` : null,
    authenticatedEmail,
  ].filter(Boolean);
  const authBusyVisible = authBusy || scanBusy;
  const showOtpBusyIndicator = otpBusy && otpCooldownSeconds <= 0;
  const sendOtpLabel =
    otpCooldownSeconds > 0 ? `${otpCooldownSeconds} 秒后重试` : otpBusy ? '发送中...' : '发送验证码';
  const scanModeLabel = authenticated ? '扫码绑定' : '扫码登录';
  const manualPanelTitle = showAuthenticatedSummary
    ? '当前账号'
    : authMode === 'register'
      ? '新用户注册'
      : authMode === 'forgot'
        ? '找回密码'
        : '用户登录';
  const manualPanelSubtitle = showAuthenticatedSummary
    ? '查看当前账号状态，并继续使用授权中心相关操作。'
    : authMode === 'register'
      ? '使用邮箱完成注册后即可进入用户中心。'
      : authMode === 'forgot'
        ? '通过邮箱验证码确认身份后重置密码。'
        : authForm.loginMethod === 'otp'
          ? '使用邮箱验证码登录当前账号。'
          : '使用邮箱或 5 位 ID 登录，并同步保留本机记住密码状态。';
  const scanNeedsRefresh =
    scanStatus === 'expired' || scanStatus === 'cancelled' || scanStatus === 'timeout';
  const scanWaitingConfirm = !scanError && (scanStatus === 'scanned' || scanStatus === 'confirmed');
  const scanCodeMatchesChannel = Boolean(scanCodeUrl) && scanCodeChannel === scanChannel;
  const scanGenerating =
    !previewMode &&
    !scanError &&
    (!scanCodeMatchesChannel || (scanBusy && !scanWaitingConfirm));
  const scanReady =
    !previewMode &&
    scanCodeMatchesChannel &&
    !scanBusy &&
    !scanWaitingConfirm &&
    !scanNeedsRefresh &&
    !scanError;
  const scanStatusPanelClassName = scanWaitingConfirm
    ? 'auth-gate-shell__scan-status-panel auth-gate-shell__scan-status-panel--success'
    : scanError
      ? 'auth-gate-shell__scan-status-panel auth-gate-shell__scan-status-panel--error'
      : 'auth-gate-shell__scan-status-panel';
  const scanCanRefresh =
    !previewMode && (Boolean(scanError) || (scanStatus !== 'scanned' && scanStatus !== 'confirmed'));
  const footerLinks = [
    { id: 'license-center', label: '打开授权中心', onClick: onOpenLicenseCenter },
    { id: 'website', label: '官方网站', onClick: onOpenWebsite },
    { id: 'niuq', label: '牛牛量化', onClick: onOpenNiuq },
    { id: 'doubao', label: '无印豆包', onClick: onOpenDoubao },
    { id: 'mini-program-preview', label: '小程序预览', onClick: onOpenMiniProgramPreview },
  ];
  const handleBack = () => {
    if (entryMode === 'manual' && !showAuthenticatedSummary && authMode !== 'login') {
      onAuthModeChange('login');
      return;
    }
    onClose();
  };

  return (
    <div className="auth-gate" role="dialog" aria-modal="true" aria-live="polite">
      <div className="auth-gate__backdrop" />
      <div className="auth-gate__stack">
        <div
          className={`auth-gate__panel auth-gate__panel--compact${
            entryMode === 'scan' ? ' auth-gate__panel--scan-free' : ''
          }`}
        >
          {closeable ? (
            <button
              type="button"
              className="auth-gate-preview-close auth-gate-preview-close--floating"
              onClick={handleBack}
              aria-label="返回上一层"
            >
              <ArrowLeft size={16} />
            </button>
          ) : null}

          <div className={`auth-gate-shell${entryMode === 'scan' ? ' auth-gate-shell--scan' : ''}`}>
            <div className="auth-gate-shell__hero">
              <div className="auth-gate-shell__title">AINIVOX-OAUTH中心</div>
            </div>

            <div
              className={`auth-gate-shell__stage${
                entryMode === 'scan' ? ' auth-gate-shell__stage--scan' : ''
              }`}
            >
              {entryMode === 'manual' ? (
                <div
                  className={`auth-gate-shell__manual-stage auth-gate-shell__manual-stage--${
                    showAuthenticatedSummary ? 'summary' : authMode
                  }`}
                >
                  <div className="auth-gate-shell__stage-copy">
                    <span className="auth-gate-shell__stage-eyebrow">
                      {showAuthenticatedSummary ? '当前账号' : '邮箱登录'}
                    </span>
                    <strong className="auth-gate-shell__stage-title">{manualPanelTitle}</strong>
                  </div>

                  <section className="auth-gate-shell__card auth-gate-shell__card--manual">
                    <div className="auth-gate-shell__card-head">
                      <strong className="auth-gate-shell__card-title">{manualPanelTitle}</strong>
                    </div>
                    {showAuthenticatedSummary ? (
                      <div className="auth-gate-shell__summary">
                        <strong>{authenticatedDisplayName}</strong>
                        <span>{subtitleParts.join(' · ') || '扫码账号待绑定邮箱'}</span>
                        <div className="auth-gate-shell__summary-actions">
                          <button
                            type="button"
                            className="sidebar-button sidebar-button--secondary"
                            onClick={onLogout}
                          >
                            {logoutBusy ? (
                              <LoaderCircle size={15} strokeWidth={1.8} className="spin" />
                            ) : (
                              <LogOut size={15} />
                            )}
                            退出登录
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {manualNotice ? (
                          <div className="auth-gate-shell__notice">{manualNotice}</div>
                        ) : null}
                        <div className="auth-gate-shell__mode-links">
                          <button
                            type="button"
                            className={`auth-gate-shell__mode-link${authMode === 'login' ? ' is-active' : ''}`}
                            onClick={() => onAuthModeChange('login')}
                          >
                            登录
                          </button>
                          <button
                            type="button"
                            className={`auth-gate-shell__mode-link${authMode === 'register' ? ' is-active' : ''}`}
                            onClick={() => onAuthModeChange('register')}
                          >
                            注册
                          </button>
                          <button
                            type="button"
                            className={`auth-gate-shell__mode-link${authMode === 'forgot' ? ' is-active' : ''}`}
                            onClick={() => onAuthModeChange('forgot')}
                          >
                            忘记密码
                          </button>
                        </div>

                        <div className="auth-gate-shell__form">
                          {authMode === 'login' ? (
                            <label className="auth-gate-shell__field">
                              <span>ID（邮箱）</span>
                              <input
                                className="license-input"
                                placeholder="输入邮箱或 5 位 ID"
                                value={authForm.identifier}
                                disabled={previewMode || authBusyVisible}
                                onChange={(event) =>
                                  onAuthFormChange({
                                    identifier: event.target.value,
                                  })
                                }
                              />
                            </label>
                          ) : (
                            <label className="auth-gate-shell__field">
                              <span>邮箱</span>
                              <input
                                className="license-input"
                                placeholder="输入邮箱"
                                value={authForm.email}
                                disabled={previewMode || authBusyVisible}
                                onChange={(event) =>
                                  onAuthFormChange({
                                    email: event.target.value,
                                  })
                                }
                              />
                            </label>
                          )}

                          {authMode === 'login' ? (
                            <div className="auth-gate-shell__login-methods">
                              <button
                                type="button"
                                className={`auth-gate-shell__mode-link${authForm.loginMethod === 'password' ? ' is-active' : ''}`}
                                tabIndex={-1}
                                onClick={() => onAuthFormChange({ loginMethod: 'password' })}
                              >
                                密码登录
                              </button>
                              <button
                                type="button"
                                className={`auth-gate-shell__mode-link${authForm.loginMethod === 'otp' ? ' is-active' : ''}`}
                                tabIndex={-1}
                                onClick={() => onAuthFormChange({ loginMethod: 'otp' })}
                              >
                                验证码登录
                              </button>
                            </div>
                          ) : null}

                          {authMode === 'login' && authForm.loginMethod === 'password' ? (
                            <label className="auth-gate-shell__field">
                              <span>密码</span>
                              <PasswordField
                                placeholder="输入密码"
                                value={authForm.password}
                                disabled={previewMode || authBusyVisible}
                                autoComplete="current-password"
                                onChange={(value) => onAuthFormChange({ password: value })}
                              />
                            </label>
                          ) : null}

                          {authMode === 'login' && authForm.loginMethod === 'otp' ? (
                            <div className="auth-gate-shell__field-inline">
                              <input
                                className="license-input"
                                placeholder="输入邮箱验证码"
                                value={authForm.verificationCode}
                                disabled={previewMode || authBusyVisible || otpBusy}
                                onChange={(event) =>
                                  onAuthFormChange({
                                    verificationCode: event.target.value,
                                  })
                                }
                              />
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary auth-gate-shell__otp-button"
                                onClick={onSendOtp}
                                disabled={
                                  previewMode ||
                                  authBusyVisible ||
                                  otpBusy ||
                                  otpCooldownSeconds > 0
                                }
                              >
                                {showOtpBusyIndicator ? (
                                  <LoaderCircle size={14} strokeWidth={1.8} className="spin" />
                                ) : null}
                                {sendOtpLabel}
                              </button>
                            </div>
                          ) : null}

                          {authMode === 'login' && authForm.loginMethod === 'password' ? (
                            <label className="auth-gate-shell__remember">
                              <input
                                type="checkbox"
                                checked={authForm.rememberMe}
                                disabled={previewMode || authBusyVisible}
                                onChange={(event) =>
                                  onAuthFormChange({
                                    rememberMe: event.target.checked,
                                  })
                                }
                              />
                              <span>记住密码</span>
                            </label>
                          ) : null}

                          {authMode === 'register' ? (
                            <div className="auth-gate-shell__field-inline">
                              <input
                                className="license-input"
                                placeholder="输入邮箱验证码"
                                value={authForm.verificationCode}
                                disabled={previewMode || authBusyVisible || otpBusy}
                                onChange={(event) =>
                                  onAuthFormChange({
                                    verificationCode: event.target.value,
                                  })
                                }
                              />
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary auth-gate-shell__otp-button"
                                onClick={onSendOtp}
                                disabled={
                                  previewMode ||
                                  authBusyVisible ||
                                  otpBusy ||
                                  otpCooldownSeconds > 0
                                }
                              >
                                {showOtpBusyIndicator ? (
                                  <LoaderCircle size={14} strokeWidth={1.8} className="spin" />
                                ) : null}
                                {sendOtpLabel}
                              </button>
                            </div>
                          ) : null}

                          {authMode === 'register' ? (
                            <label className="auth-gate-shell__field">
                              <span>密码</span>
                              <PasswordField
                                placeholder="输入密码"
                                value={authForm.password}
                                disabled={previewMode || authBusyVisible}
                                autoComplete="new-password"
                                onChange={(value) => onAuthFormChange({ password: value })}
                              />
                            </label>
                          ) : null}

                          {authMode === 'register' ? (
                            <label className="auth-gate-shell__field">
                              <span>确认密码</span>
                              <PasswordField
                                placeholder="再次输入密码"
                                value={authForm.confirmPassword}
                                disabled={previewMode || authBusyVisible}
                                autoComplete="new-password"
                                onChange={(value) => onAuthFormChange({ confirmPassword: value })}
                              />
                            </label>
                          ) : null}

                          {authMode === 'forgot' ? (
                            <div className="auth-gate-shell__field-inline">
                              <input
                                className="license-input"
                                placeholder="输入邮箱验证码"
                                value={authForm.verificationCode}
                                disabled={previewMode || authBusyVisible || otpBusy}
                                onChange={(event) =>
                                  onAuthFormChange({
                                    verificationCode: event.target.value,
                                  })
                                }
                              />
                              <button
                                type="button"
                                className="sidebar-button sidebar-button--secondary auth-gate-shell__otp-button"
                                onClick={onSendOtp}
                                disabled={
                                  previewMode ||
                                  authBusyVisible ||
                                  otpBusy ||
                                  otpCooldownSeconds > 0
                                }
                              >
                                {showOtpBusyIndicator ? (
                                  <LoaderCircle size={14} strokeWidth={1.8} className="spin" />
                                ) : null}
                                {sendOtpLabel}
                              </button>
                            </div>
                          ) : null}

                          {authMode === 'forgot' ? (
                            <label className="auth-gate-shell__field">
                              <span>新密码</span>
                              <PasswordField
                                placeholder="输入新密码"
                                value={authForm.password}
                                disabled={previewMode || authBusyVisible}
                                autoComplete="new-password"
                                onChange={(value) => onAuthFormChange({ password: value })}
                              />
                            </label>
                          ) : null}

                          {authMode === 'forgot' ? (
                            <label className="auth-gate-shell__field">
                              <span>确认新密码</span>
                              <PasswordField
                                placeholder="再次输入新密码"
                                value={authForm.confirmPassword}
                                disabled={previewMode || authBusyVisible}
                                autoComplete="new-password"
                                onChange={(value) => onAuthFormChange({ confirmPassword: value })}
                              />
                            </label>
                          ) : null}

                          {authMode === 'register' ? (
                            <label className="auth-gate-shell__field">
                              <span>邀请码</span>
                              <input
                                className="license-input"
                                placeholder="没有可留空"
                                value={authForm.inviteCode}
                                disabled={previewMode || authBusyVisible}
                                onChange={(event) =>
                                  onAuthFormChange({
                                    inviteCode: event.target.value,
                                  })
                                }
                              />
                            </label>
                          ) : null}

                          <button
                            type="button"
                            className="sidebar-button auth-gate-shell__primary"
                            onClick={onSubmitAuth}
                            disabled={previewMode || authBusyVisible}
                          >
                            {authBusyVisible ? (
                              <LoaderCircle size={15} strokeWidth={1.8} className="spin" />
                            ) : (
                              <UserRound size={15} />
                            )}
                            {getPrimaryLabel(authMode)}
                          </button>
                        </div>
                      </>
                    )}
                  </section>
                </div>
              ) : (
                <section className="auth-gate-shell__scan-stage auth-gate-shell__scan-stage--standalone">
                  <div className="auth-gate-shell__scan-card">
                      <div className="auth-gate-shell__scan-box">
                        {scanReady ? (
                          <img
                            key={`${scanChannel}:${scanCodeUrl ?? 'empty'}`}
                            src={scanCodeUrl ?? undefined}
                            alt={`${scanModeLabel}二维码`}
                            className="auth-gate-shell__scan-image"
                        />
                      ) : null}

                      {previewMode ? (
                        <div className="auth-gate-shell__scan-placeholder">
                          <QrCode size={52} />
                          <strong className="auth-gate-shell__scan-state-title">{scanModeLabel}预览</strong>
                          <span className="auth-gate-shell__scan-state-note">{scanStatusHint}</span>
                        </div>
                      ) : null}

                      {scanGenerating ? (
                        <div className="auth-gate-shell__scan-placeholder auth-gate-shell__scan-placeholder--loading">
                          <div className="auth-gate-shell__scan-loading-center">
                            <LoaderCircle
                              size={56}
                              strokeWidth={2.2}
                              className="spin auth-gate-shell__scan-placeholder-spinner"
                            />
                            <strong className="auth-gate-shell__scan-state-title">
                              正在生成二维码
                            </strong>
                          </div>
                        </div>
                      ) : null}

                      {!previewMode && !scanGenerating && !scanReady ? (
                        <div className={scanStatusPanelClassName}>
                          {scanWaitingConfirm ? (
                            <div className="auth-gate-shell__scan-success-visual">
                              <div className="auth-gate-shell__scan-success-orb">
                                <div className="auth-gate-shell__scan-success-core">
                                  <Check size={56} strokeWidth={1.9} />
                                </div>
                                <span className="auth-gate-shell__scan-success-badge">
                                  <Check size={18} strokeWidth={2.4} />
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="auth-gate-shell__scan-state-visual">
                              {scanBusy ? (
                                <LoaderCircle
                                  size={44}
                                  strokeWidth={2}
                                  className="spin auth-gate-shell__scan-placeholder-spinner"
                                />
                              ) : scanError || scanStatus === 'cancelled' ? (
                                <X
                                  size={30}
                                  strokeWidth={2.1}
                                  className="auth-gate-shell__scan-state-icon is-danger"
                                />
                              ) : (
                                <QrCode
                                  size={30}
                                  strokeWidth={1.9}
                                  className="auth-gate-shell__scan-state-icon"
                                />
                              )}
                            </div>
                          )}

                          <strong className="auth-gate-shell__scan-state-heading">{scanStatusTitle}</strong>
                          <span className="auth-gate-shell__scan-state-copy">{scanStatusHint}</span>

                          {scanCanRefresh ? (
                            <button
                              type="button"
                              className="auth-gate-shell__scan-refresh"
                              onClick={onRetryScan}
                              disabled={scanBusy}
                            >
                              {scanBusy ? '刷新中' : scanNeedsRefresh || scanError ? '刷新二维码' : '重新获取二维码'}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="auth-gate-shell__scan-channel-switch">
                      <button
                        type="button"
                        className={scanChannel === 'mini_program' ? 'is-active' : ''}
                        onClick={() => onScanChannelChange('mini_program')}
                        disabled={scanBusy}
                      >
                        小程序扫码
                      </button>
                      <button
                        type="button"
                        className={scanChannel === 'official_account' ? 'is-active' : ''}
                        onClick={() => onScanChannelChange('official_account')}
                        disabled={scanBusy}
                      >
                        公众号二维码登录
                      </button>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>

        <div className="auth-gate__dock">
          <div className="auth-gate-shell__switch-row">
            <div className="auth-gate-shell__switch">
              <button
                type="button"
                className={`auth-gate-shell__switch-button${entryMode === 'manual' ? ' is-active' : ''}`}
                onClick={() => onEntryModeChange('manual')}
              >
                手动登录
              </button>
              <button
                type="button"
                className={`auth-gate-shell__switch-button${entryMode === 'scan' ? ' is-active' : ''}`}
                onClick={() => onEntryModeChange('scan')}
              >
                {scanModeLabel}
              </button>
            </div>
          </div>

          <div className="auth-gate-shell__footer-links">
            {footerLinks.map((item) => (
              <button
                key={item.id}
                type="button"
                className="auth-gate-shell__footer-link"
                onClick={item.onClick}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
